/**
 * heyAPI Client Configuration
 *
 * Configures the auto-generated heyAPI client with:
 * - fetchWithToken for authenticated requests (or plain fetch for tests)
 * - Sentry error reporting for 5xx server errors
 * - APIError throwing on non-2xx responses
 * - Custom vizit-* header extraction
 */

import * as Sentry from '@sentry/react';
import { APIError, fetchWithToken } from '@vizit/auth';
import { API_BASE_URL, SENTRY_FEATURE_TAGS } from '@vizit/constants';

import { client } from './generated/client.gen';

client.setConfig({
  baseUrl: API_BASE_URL,
  fetch: import.meta.env.VITEST ? (...args: Parameters<typeof fetch>) => fetch(...args) : fetchWithToken,
  throwOnError: true,
});

// Error interceptor: create APIError, capture 5xx in Sentry
client.interceptors.error.use((error, response, request, _options) => {
  // Network errors or aborts won't have a response
  if (!response || !request) {
    return error;
  }

  const apiError = new APIError(typeof error === 'string' ? error : JSON.stringify(error), response, request);

  Sentry.withScope((scope) => {
    scope.setFingerprint([request.method, request.url, String(response.status)]);

    if (response.status >= 500) {
      Sentry.captureException(apiError, {
        tags: { feature: SENTRY_FEATURE_TAGS.Api },
        level: 'warning',
      });
    }
  });

  return apiError;
});

export { client };
